/**
 * MarketsPage Component
 *
 * Main Markets page combining:
 * - Daily Summary (smart summary card or NotificationCTA)
 * - Market Pulse (indicators + treemap)
 * - Market News list
 * - Feedback footer
 */

import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { css } from '@emotion/react';
import { useHistory, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useTranslation } from 'react-i18next';
import { colors, spacing, fontStyles } from '../../styles';
import Page from '../Page';
import StickyHeader from '../StickyHeader';
import NotificationBadge from '../NotificationBadge/NotificationBadge';
import { BellIcon } from '../Icons';
import { MarketIndicators, MarketIndicatorsSkeleton } from '../MarketIndicators';
import MarketMap from '../MarketMap';
import MarketNewsList from '../MarketNewsList';
import MarketSummaryCard from '../MarketSummaryCard';
import FeedbackFooter from '../FeedbackFooter';
import MarketUpdatesBottomSheet from '../MarketUpdatesBottomSheet';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import {
  getTodaysSmartSummary,
  getMarketSentiment,
  getGeneralMarketNews,
  markSmartSummaryAsRead,
  generateSmartSummary,
  PortfolioNewsArticle,
} from '../../apis/backend';
import { PushNotificationService } from '../../services/pushNotificationService';
import * as stores from '../../stores';
import infoIcon from '../Icons/info.svg';

// Device ID storage key constant
const DEVICE_ID_KEY = 'bloom_device_id';

/**
 * Get or generate a device token for API requests
 */
const getDeviceToken = async (): Promise<string | null> => {
  try {
    let deviceToken = await PushNotificationService.getToken();

    if (!deviceToken) {
      const platform = Capacitor.getPlatform();

      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FirebaseMessaging')) {
        try {
          const { token } = await FirebaseMessaging.getToken();
          deviceToken = token;
        } catch (error) {
          console.warn('[getDeviceToken] Failed to get FCM token:', error);
        }
      } else if (platform === 'web') {
        let storedId = localStorage.getItem(DEVICE_ID_KEY);
        if (!storedId) {
          storedId = `web_${crypto.randomUUID()}`;
          localStorage.setItem(DEVICE_ID_KEY, storedId);
        }
        deviceToken = storedId;
      }
    }

    if (!deviceToken) {
      deviceToken = localStorage.getItem('dev_device_token');
    }

    return deviceToken;
  } catch (error) {
    console.error('[getDeviceToken] Error:', error);
    return null;
  }
};

const MarketsPage: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isMarketPulseInfoOpen, setIsMarketPulseInfoOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { areAllEnabled } = useNotificationPreferences();

  // Get portfolio data from store for smart summary generation
  const { portfolios } = stores.Portfolios?.useState?.() ?? { portfolios: [] };

  // Get all symbols from all portfolios for smart summary generation
  const allSymbols = useMemo(() => {
    const symbols = new Set<string>();
    (portfolios ?? []).forEach((portfolio) => {
      (portfolio.allocations || []).forEach((a) => symbols.add(a.symbol));
      (portfolio.symbols || []).forEach((s) => symbols.add(s));
    });
    return Array.from(symbols);
  }, [portfolios]);

  // Use refs for synchronous tracking to prevent race conditions
  const hasAttemptedGenerationRef = useRef(false);
  const prevSymbolsRef = useRef<string>('');
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  // Reset generation attempt when portfolio symbols change so a new summary is generated
  useEffect(() => {
    const symbolsKey = allSymbols.join(',');
    if (prevSymbolsRef.current && prevSymbolsRef.current !== symbolsKey) {
      hasAttemptedGenerationRef.current = false;
    }
    prevSymbolsRef.current = symbolsKey;
  }, [allSymbols]);

  // Fetch smart summary with device token
  const {
    data: smartSummary,
    isLoading: isSmartSummaryLoading,
    refetch: refetchSmartSummary,
  } = useQuery(
    ['todaysSmartSummary'],
    async () => {
      const deviceToken = await getDeviceToken();
      if (!deviceToken) return null;
      const summary = await getTodaysSmartSummary(deviceToken);
      if (summary) {
        // Mark as read in background
        markSmartSummaryAsRead(summary.id, deviceToken).catch(() => undefined);
      }
      return summary;
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Reset generation attempt when summary becomes null (e.g. new day, no summary yet)
  useEffect(() => {
    if (!smartSummary && !isSmartSummaryLoading) {
      hasAttemptedGenerationRef.current = false;
    }
  }, [smartSummary, isSmartSummaryLoading]);

  // Auto-generate smart summary if none exists and we have portfolio symbols
  useEffect(() => {
    // Reset mounted state on effect run (dependency change)
    isMountedRef.current = true;

    const attemptGeneration = async () => {
      // Skip if: loading, already have summary, already attempted, or no symbols
      if (isSmartSummaryLoading || smartSummary || hasAttemptedGenerationRef.current || allSymbols.length === 0) {
        return;
      }

      // Mark as attempted immediately (synchronously) to prevent race conditions
      hasAttemptedGenerationRef.current = true;
      setIsGenerating(true);

      const deviceToken = await getDeviceToken();
      if (!deviceToken) {
        setIsGenerating(false);
        return;
      }

      const result = await generateSmartSummary(deviceToken, allSymbols);
      // Check if component is still mounted after async operation
      if (!isMountedRef.current) {
        setIsGenerating(false);
        return;
      }

      if (result.status === 'processing') {
        // Poll for the generated summary (generation takes a few seconds)
        let attempts = 0;
        const maxAttempts = 6; // Poll for up to 30 seconds (6 * 5s)
        pollIntervalRef.current = setInterval(async () => {
          if (!isMountedRef.current) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            return;
          }
          attempts++;
          try {
            const { data } = await refetchSmartSummary();
            if (data || attempts >= maxAttempts) {
              setIsGenerating(false);
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
            }
          } catch {
            // Stop polling on error to prevent repeated failed calls
            setIsGenerating(false);
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
        }, 5000);
      } else {
        setIsGenerating(false);
      }
    };

    attemptGeneration();

    // Cleanup interval on unmount or dependency change
    return () => {
      isMountedRef.current = false;
      setIsGenerating(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isSmartSummaryLoading, smartSummary, allSymbols, refetchSmartSummary]);

  // Fetch market sentiment
  const sentimentQuery = useQuery(['market', 'sentiment'], getMarketSentiment, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch market news
  const newsQuery = useQuery(
    ['market', 'news'],
    async () => {
      const response = await getGeneralMarketNews();
      return response.articles || [];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  return (
    <Page showNavigationBar>
      <StickyHeader>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          `}
        >
          <h1
            css={css`
              font-size: 1.38rem;
              line-height: 1.88rem;
              font-weight: 500;
              margin: 0;
              color: var(--text-color);
            `}
          >
            {t('markets.title')}
          </h1>
          <NotificationBadge>
            <button
              onClick={() => history.push('/notifications')}
              aria-label={t('markets.viewAlerts')}
              css={css`
                background: none;
                border: none;
                padding: ${spacing.milli};
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                transition: background-color 0.2s;
                &:hover {
                  background-color: rgba(0, 0, 0, 0.05);
                }
                html[data-theme='dark'] &:hover {
                  background-color: rgba(255, 255, 255, 0.1);
                }
              `}
            >
              <span
                css={css`
                  color: ${colors.petrolBlue};
                  display: flex;
                `}
              >
                <BellIcon size={20} />
              </span>
            </button>
          </NotificationBadge>
        </div>
      </StickyHeader>

      <div
        css={css`
          max-width: 600px;
          margin: 0 auto;
        `}
      >
        {/* Daily Summary Section */}
        <section>
          <MarketSummaryCard
            summary={smartSummary ?? null}
            isLoading={isSmartSummaryLoading || isGenerating}
            notificationsEnabled={areAllEnabled}
          />
        </section>

        {/* Market Pulse Section */}
        <section
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.milli};
              margin-bottom: ${spacing.centi};
            `}
          >
            <h2
              css={css`
                ${fontStyles.lion}
                margin: 0;
                color: var(--text-color);
              `}
            >
              {t('markets.marketPulse')}
            </h2>
            <img
              src={infoIcon}
              alt="About Market Pulse"
              role="button"
              tabIndex={0}
              onClick={() => setIsMarketPulseInfoOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMarketPulseInfoOpen(true);
                }
              }}
              css={css`
                cursor: pointer;
                width: 18px;
                height: 18px;
                opacity: 0.7;
                &:hover {
                  opacity: 1;
                }
              `}
            />
          </div>

          {/* Market Indicators */}
          <div
            css={css`
              margin-bottom: ${spacing.centi};
            `}
          >
            {sentimentQuery.isLoading ? (
              <MarketIndicatorsSkeleton />
            ) : (
              sentimentQuery.data && <MarketIndicators indicators={sentimentQuery.data} />
            )}
          </div>

          {/* Market Heatmap */}
          <MarketMap />
        </section>

        {/* Market News Section */}
        <section
          css={css`
            margin-top: ${spacing.hecto};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
            `}
          >
            <h2
              css={css`
                ${fontStyles.lion}
                margin: 0;
                color: var(--text-color);
              `}
            >
              {t('markets.marketNews')}
            </h2>
            <Link
              to="/ideas/news"
              css={css`
                ${fontStyles.mouse}
                color: ${colors.petrolBlue};
                text-decoration: none;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {t('markets.allNews')}
            </Link>
          </div>

          <MarketNewsList
            newsArticles={(newsQuery.data as PortfolioNewsArticle[]) || []}
            isLoading={newsQuery.isLoading}
          />
        </section>

        {/* Feedback Footer */}
        <FeedbackFooter />
      </div>

      {/* Market Updates Explanation Modal */}
      <MarketUpdatesBottomSheet isOpen={isMarketPulseInfoOpen} onClose={() => setIsMarketPulseInfoOpen(false)} />
    </Page>
  );
};

export default MarketsPage;
